import React, { useState, useEffect } from 'react';
import { useUserStore } from '../stores/useUserStore';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useRBAC } from '../hooks/useRBAC';
import { shiftService, Shift as ApiShift } from '../services/shiftService';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Target,
  Clock,
  Calendar,
  MapPin,
  Users,
  ChevronLeft,
  ChevronRight,
  Bell,
  AlertCircle,
} from 'lucide-react';
import {
  format,
  startOfWeek,
  endOfWeek,
  addDays,
  isToday,
  isTomorrow,
} from 'date-fns';

interface Shift {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  date: string;
  location: string;
  role: string;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  notes?: string;
  userId: string;
  staffName?: string;
}

interface ScheduleData {
  currentWeek: Shift[];
  upcomingShifts: Shift[];
  totalHours: number;
  nextShift?: Shift;
}

const ModernSchedule: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user } = useUserStore();
  const { userRoles } = useRBAC();
  const [scheduleData, setScheduleData] = useState<ScheduleData>({
    currentWeek: [],
    upcomingShifts: [],
    totalHours: 0,
  });
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScheduleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeek, user]); // Keep user dep to trigger re-renders, userRoles will update via hook

  // Transform API shift to component shift format
  const transformShift = (apiShift: ApiShift): Shift => {
    const startTime = new Date(apiShift.startTime);
    const endTime = new Date(apiShift.endTime);

    return {
      id: apiShift.id,
      title: apiShift.title || 'Shift',
      startTime: format(startTime, 'HH:mm'),
      endTime: format(endTime, 'HH:mm'),
      date: format(startTime, 'yyyy-MM-dd'),
      location: 'Main Store', // TODO: Get from shift data when available
      role: apiShift.position || 'Staff',
      status: apiShift.status,
      notes: apiShift.description,
      userId: apiShift.staffId,
      staffName: apiShift.staffName,
    };
  };

  const loadScheduleData = async () => {
      setLoading(true);
    try {
      const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });

      // Fetch ALL shifts instead of just user's shifts
      const allShifts = await shiftService.getShifts();

      // Transform API shifts to component format
      const transformedShifts = allShifts.map(transformShift);

      // Filter shifts for current week
      const today = new Date();
      let allowedEndDateStr: string | null = null;
      let allowedStartDateStr: string | null = null;
      
      const isStaff = userRoles && 
        !userRoles.includes('admin') && 
        !userRoles.includes('sysAdmin') && 
        !userRoles.includes('orgAdmin') && 
        !userRoles.includes('storeManager');

      if (isStaff) {
        const isSunday = today.getDay() === 0;
        
        // Allowed start is start of week (Sunday-based) to hide previous Saturday
        const allowedStartDate = startOfWeek(today, { weekStartsOn: 0 });
        allowedStartDateStr = format(allowedStartDate, 'yyyy-MM-dd');
        
        // Allowed end is end of week (Monday-based) to include next Sunday
        const currentWeekEnd = endOfWeek(today, { weekStartsOn: 1 });
        let requestEndDate = currentWeekEnd;
        if (isSunday) {
          requestEndDate = addDays(currentWeekEnd, 7);
        }
        allowedEndDateStr = format(requestEndDate, 'yyyy-MM-dd');
      }

      const currentWeekShifts = transformedShifts.filter((shift) => {
        const shiftDate = new Date(shift.date);
        
        // Standard current week filter for the view
        const inCurrentWeekView = shiftDate >= weekStart && shiftDate <= weekEnd;
        
        // Apply role-based restriction if applicable
        if (isStaff && allowedStartDateStr && allowedEndDateStr) {
          // If the shift is outside the allowed range, hide it
          // UNLESS it is the user's own shift
          
          if (shift.userId !== user?.id) {
             // String comparison works correctly for YYYY-MM-DD
             if (shift.date < allowedStartDateStr || shift.date > allowedEndDateStr) {
               return false;
             }
          }
        }
        
        return inCurrentWeekView;
      });

      // Get upcoming shifts for current user ONLY (for "Next Shift" widget)
      // We do NOT restrict upcoming shifts for the user themselves
      const myUpcomingShifts = transformedShifts
        .filter((shift) => {
          const shiftDate = new Date(shift.date);
          return shift.userId === user?.id && shiftDate > today;
        })
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA.getTime() - dateB.getTime();
        })
        .slice(0, 3);

      // Calculate total hours for the week for current user ONLY
      const myCurrentWeekShifts = currentWeekShifts.filter(
        (shift) => shift.userId === user?.id
      );
      
      const totalHours = myCurrentWeekShifts.reduce((total, shift) => {
        const start = new Date(`2000-01-01T${shift.startTime}`);
        const end = new Date(`2000-01-01T${shift.endTime}`);
        // Handle end time that's next day (e.g., 24:00 = 00:00 next day)
        if (end < start) {
          end.setDate(end.getDate() + 1);
        }
        const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return total + hours;
      }, 0);

      setScheduleData({
        currentWeek: currentWeekShifts, // Contains ALL shifts for the week
        upcomingShifts: myUpcomingShifts, // Contains only MY upcoming shifts
        totalHours, // Calculated based on MY shifts
        nextShift: myUpcomingShifts[0],
      });
    } catch (error) {
      console.error('Error loading schedule:', error);
      notify(
        translate(
          'pos.staff.schedule.failed_to_load',
          'Failed to load schedule data'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    setCurrentWeek((prev) => {
      const newWeek = new Date(prev);
      newWeek.setDate(prev.getDate() + (direction === 'next' ? 7 : -7));
      return newWeek;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatShiftTime = (startTime: string, endTime: string) => {
    return `${startTime} - ${endTime}`;
  };

  const translateShiftTitle = (title: string) => {
    switch (title) {
      case 'Morning Shift':
        return translate(
          'pos.staff.schedule.shift_title_morning',
          'Morning Shift'
        );
      case 'Evening Shift':
        return translate(
          'pos.staff.schedule.shift_title_evening',
          'Evening Shift'
        );
      case 'Weekend Shift':
        return translate(
          'pos.staff.schedule.shift_title_weekend',
          'Weekend Shift'
        );
      default:
        return title;
    }
  };

  const translateLocation = (location: string) => {
    switch (location) {
      case 'Main Store':
        return translate(
          'pos.staff.schedule.location_main_store',
          'Main Store'
        );
      default:
        return location;
    }
  };

  const translateRole = (role: string) => {
    switch (role) {
      case 'Barista':
        return translate('pos.staff.schedule.role_barista', 'Barista');
      case 'Cashier':
        return translate('pos.staff.schedule.role_cashier', 'Cashier');
      case 'Manager':
        return translate('pos.staff.schedule.role_manager', 'Manager');
      default:
        return role;
    }
  };

  const getWeekDays = () => {
    const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-2">
            {translate('pos.staff.schedule.loading', 'Loading schedule...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-foreground">
          {translate('pos.staff.schedule.title', 'Working Schedule')}
        </h1>
        <p className="text-muted-foreground mt-2">
          {translate(
            'pos.staff.schedule.subtitle',
            'View assigned shifts and working hours'
          )}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-muted-foreground">
                  {translate('pos.staff.schedule.stats_this_week', 'My Hours')}
                </p>
                <p className="text-2xl font-bold">
                  {scheduleData.totalHours.toFixed(1)}h
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-muted-foreground">
                  {translate('pos.staff.schedule.stats_shifts', 'My Shifts')}
                </p>
                <p className="text-2xl font-bold">
                  {scheduleData.currentWeek.filter(s => s.userId === user?.id).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm text-muted-foreground">
                  {translate(
                    'pos.staff.schedule.stats_next_shift',
                    'My Next Shift'
                  )}
                </p>
                <p className="text-lg font-semibold">
                  {scheduleData.nextShift
                    ? `${scheduleData.nextShift.startTime} ${new Date(
                        scheduleData.nextShift.date
                      ).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}`
                    : translate('pos.staff.schedule.stats_none', 'None')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Week Navigation */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateWeek('prev')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h3 className="text-lg font-semibold">
                {startOfWeek(currentWeek, {
                  weekStartsOn: 1,
                }).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}{' '}
                -{' '}
                {endOfWeek(currentWeek, { weekStartsOn: 1 }).toLocaleDateString(
                  undefined,
                  { month: 'short', day: 'numeric', year: 'numeric' }
                )}
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateWeek('next')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="outline"
              onClick={() => setCurrentWeek(new Date())}
            >
              {translate('pos.staff.schedule.nav_today', 'Today')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Week Calendar View */}
          <div className="grid grid-cols-7 gap-2 mb-4">
            {getWeekDays().map((day, index) => {
              const dayShifts = scheduleData.currentWeek.filter(
                (shift) =>
                  format(new Date(shift.date), 'yyyy-MM-dd') ===
                  format(day, 'yyyy-MM-dd')
              );
              
              const myDayShiftsCount = dayShifts.filter(s => s.userId === user?.id).length;
              const hasMyShift = myDayShiftsCount > 0;

              return (
                <div key={index} className="text-center">
                  <div
                    className={`p-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors ${
                      isToday(day)
                        ? 'bg-primary text-primary-foreground'
                        : selectedDay &&
                          selectedDay.toDateString() === day.toDateString()
                        ? 'bg-blue-100 border-2 border-blue-500'
                        : hasMyShift 
                          ? 'bg-green-50 border border-green-200'
                          : 'bg-muted'
                    }`}
                    onClick={() => {
                      console.log('Day clicked:', format(day, 'yyyy-MM-dd'));
                      setSelectedDay(day);
                      notify(
                        translate(
                          'pos.staff.schedule.selected_day_notification',
                          'Selected %{date}'
                        ).replace(
                          '%{date}',
                          day.toLocaleDateString(undefined, {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        ),
                        { type: 'info' }
                      );
                    }}
                  >
                    <p className="text-sm font-medium">
                      {day.toLocaleDateString(undefined, { weekday: 'short' })}
                    </p>
                    <p className="text-lg font-bold">
                      {day.toLocaleDateString(undefined, { day: 'numeric' })}
                    </p>
                  </div>
                  {dayShifts.length > 0 && (
                    <div className="mt-1 flex justify-center gap-1">
                      <Badge variant={hasMyShift ? "default" : "secondary"} className="text-xs">
                        {dayShifts.length}
                      </Badge>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected Day Details */}
      {selectedDay && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {selectedDay.toLocaleDateString(undefined, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const dayShifts = scheduleData.currentWeek.filter(
                (shift) =>
                  format(new Date(shift.date), 'yyyy-MM-dd') ===
                  format(selectedDay, 'yyyy-MM-dd')
              );

              if (dayShifts.length === 0) {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>
                      {translate(
                        'pos.staff.schedule.no_shifts_for_day',
                        'No shifts scheduled for this day'
                      )}
                    </p>
                  </div>
                );
              }

              return (
                <div className="space-y-3">
                  {dayShifts.map((shift) => {
                    const isMyShift = shift.userId === user?.id;
                    return (
                    <div
                      key={shift.id}
                      className={`flex items-center justify-between p-3 border rounded-lg ${isMyShift ? 'bg-green-50 border-green-200 shadow-sm' : ''}`}
                    >
                      <div>
                        {isMyShift && (
                          <div className="mb-1">
                             <Badge className="bg-green-600 hover:bg-green-700 text-xs py-0 h-5">Me</Badge>
                          </div>
                        )}
                        <h4 className="font-medium flex items-center gap-2">
                          {translateShiftTitle(shift.title)}
                          <span className="text-xs font-normal text-muted-foreground">
                            ({shift.staffName || 'Unknown Staff'})
                          </span>
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {shift.startTime} - {shift.endTime}
                        </p>
                      </div>
                      <Badge
                        variant={
                          shift.status === 'confirmed'
                            ? 'default'
                            : shift.status === 'scheduled'
                            ? 'secondary'
                            : 'destructive'
                        }
                      >
                        {shift.status === 'scheduled'
                          ? translate(
                              'pos.staff.schedule.status_scheduled',
                              'Scheduled'
                            )
                          : shift.status === 'confirmed'
                          ? translate(
                              'pos.staff.schedule.status_confirmed',
                              'Confirmed'
                            )
                          : shift.status === 'completed'
                          ? translate(
                              'pos.staff.schedule.status_completed',
                              'Completed'
                            )
                          : translate(
                              'pos.staff.schedule.status_cancelled',
                              'Cancelled'
                            )}
                      </Badge>
                    </div>
                  )})}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Current Week Shifts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            {translate(
              'pos.staff.schedule.this_week_shifts_title',
              "This Week's Schedule"
            )}
          </CardTitle>
          <CardDescription>
            {translate(
              'pos.staff.schedule.this_week_shifts_description',
              'All scheduled shifts for the current week'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {scheduleData.currentWeek.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {translate(
                  'pos.staff.schedule.no_shifts_for_week',
                  'No shifts scheduled for this week'
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {scheduleData.currentWeek.map((shift) => {
                const isMyShift = shift.userId === user?.id;
                return (
                <div
                  key={shift.id}
                  className={`flex items-center justify-between p-4 border rounded-lg ${isMyShift ? 'bg-green-50 border-green-200 border-l-4 border-l-green-500 shadow-sm' : ''}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">
                        {new Date(shift.date).toLocaleDateString(undefined, {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                      <p className="font-semibold">
                        {formatShiftTime(shift.startTime, shift.endTime)}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {translateShiftTitle(shift.title)}
                        </p>
                        {isMyShift && <Badge className="bg-green-600 hover:bg-green-700 h-5 text-[10px] px-1.5">YOU</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                        <Users className="h-3 w-3" />
                         <span className={isMyShift ? "font-medium text-green-700" : ""}>
                          {shift.staffName || 'Unknown Staff'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                        <MapPin className="h-3 w-3" />
                        {translateLocation(shift.location)}
                        <span className="mx-1">•</span>
                        <span>{translateRole(shift.role)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(shift.status)}>
                      {shift.status === 'scheduled'
                        ? translate(
                            'pos.staff.schedule.status_scheduled',
                            'Scheduled'
                          )
                        : shift.status === 'confirmed'
                        ? translate(
                            'pos.staff.schedule.status_confirmed',
                            'Confirmed'
                          )
                        : shift.status === 'completed'
                        ? translate(
                            'pos.staff.schedule.status_completed',
                            'Completed'
                          )
                        : translate(
                            'pos.staff.schedule.status_cancelled',
                            'Cancelled'
                          )}
                    </Badge>
                    {shift.notes && (
                      <Button variant="ghost" size="sm">
                        <AlertCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              )})}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Shifts (My Shifts Only) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {translate('pos.staff.schedule.upcoming_shifts', 'My Upcoming Shifts')}
          </CardTitle>
          <CardDescription>
            {translate(
              'pos.staff.schedule.upcoming_shifts_description',
              'Your next scheduled shifts'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {scheduleData.upcomingShifts.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {translate(
                  'pos.staff.schedule.no_upcoming_shifts',
                  'No upcoming shifts'
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {scheduleData.upcomingShifts.map((shift) => (
                <div
                  key={shift.id}
                  className="flex items-center justify-between p-4 border rounded-lg bg-green-50 border-green-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">
                        {isToday(new Date(shift.date))
                          ? translate('pos.staff.schedule.today_label', 'Today')
                          : isTomorrow(new Date(shift.date))
                          ? translate(
                              'pos.staff.schedule.tomorrow_label',
                              'Tomorrow'
                            )
                          : new Date(shift.date).toLocaleDateString(undefined, {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                      </p>
                      <p className="font-semibold">
                        {formatShiftTime(shift.startTime, shift.endTime)}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">
                        {translateShiftTitle(shift.title)}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        {translateLocation(shift.location)}
                        <span className="mx-1">•</span>
                        <Users className="h-4 w-4" />
                        {translateRole(shift.role)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(shift.status)}>
                      {shift.status === 'scheduled'
                        ? translate(
                            'pos.staff.schedule.status_scheduled',
                            'Scheduled'
                          )
                        : shift.status === 'confirmed'
                        ? translate(
                            'pos.staff.schedule.status_confirmed',
                            'Confirmed'
                          )
                        : shift.status === 'completed'
                        ? translate(
                            'pos.staff.schedule.status_completed',
                            'Completed'
                          )
                        : translate(
                            'pos.staff.schedule.status_cancelled',
                            'Cancelled'
                          )}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModernSchedule;
